/**
 * Servicio centralizado para manejar favoritos con sincronización en tiempo real
 */

import { Storage } from '@capacitor/storage';
import { firebase } from '../config/firebase.config';

export class FavoritesService {
  private static instance: FavoritesService;
  private unsubscribe: (() => void) | null = null;
  private currentUserId: string | null = null;
  private listeners: Array<(favorites: string[]) => void> = [];

  private constructor() {}

  static getInstance(): FavoritesService {
    if (!FavoritesService.instance) {
      FavoritesService.instance = new FavoritesService();
    }
    return FavoritesService.instance;
  }

  /**
   * Inicializar el servicio con un usuario específico
   */
  async initialize(userId: string): Promise<void> {
    console.log('FavoritesService: Inicializando para usuario:', userId);
    console.log('FavoritesService: Usuario actual:', this.currentUserId);
    console.log('FavoritesService: Listener activo:', !!this.unsubscribe);

    // Si ya está inicializado para el mismo usuario, solo recargar favoritos
    if (this.currentUserId === userId && this.unsubscribe) {
      console.log('FavoritesService: Ya inicializado para este usuario, recargando favoritos...');
      await this.loadFavoritesFromStorage();
      return;
    }

    // Si ya hay un listener activo para otro usuario, desconectarlo
    if (this.unsubscribe && this.currentUserId !== userId) {
      console.log('FavoritesService: Desconectando listener anterior para usuario:', this.currentUserId);
      this.unsubscribe();
      this.unsubscribe = null;
    }

    this.currentUserId = userId;

    // Primero cargar favoritos desde Storage local (sincronizados por getUserData)
    await this.loadFavoritesFromStorage();

    // Luego configurar listener en tiempo real
    await this.setupRealtimeListener();

    // Notificar a listeners existentes que el servicio está activo
    console.log('FavoritesService: Notificando a', this.listeners.length, 'listeners existentes');
    if (this.listeners.length > 0) {
      // Usar un pequeño delay para asegurar que los componentes estén listos
      setTimeout(async () => {
        try {
          const currentFavorites = await this.getCurrentFavorites();
          console.log('FavoritesService: Notificando favoritos a listeners:', currentFavorites);
          this.notifyListeners(currentFavorites);
        } catch (error) {
          console.error('FavoritesService: Error al notificar listeners:', error);
        }
      }, 100);
    }

    console.log('FavoritesService: Inicialización completada para usuario:', userId);
  }

  /**
   * Cargar favoritos desde Storage local
   */
  private async loadFavoritesFromStorage(): Promise<void> {
    try {
      console.log('FavoritesService: Cargando favoritos desde Storage local para usuario:', this.currentUserId);

      const favoritesData = await Storage.get({ key: 'FAVORITOS' });
      const favoritesString = favoritesData.value || '';

      console.log('FavoritesService: Datos en Storage FAVORITOS:', favoritesString);

      if (favoritesString) {
        const favorites = favoritesString.split(',').filter((id: string) => id.trim() !== '');
        console.log('FavoritesService: Favoritos procesados desde Storage:', favorites);

        // Notificar a los listeners inmediatamente
        this.notifyListeners(favorites);
        console.log('FavoritesService: Notificados', this.listeners.length, 'listeners con favoritos desde Storage');
      } else {
        console.log('FavoritesService: No hay favoritos en Storage local, notificando array vacío');
        this.notifyListeners([]);
      }
    } catch (error) {
      console.error('FavoritesService: Error al cargar favoritos desde Storage:', error);
      this.notifyListeners([]);
    }
  }

  /**
   * Configurar listener en tiempo real de Firestore
   */
  private async setupRealtimeListener(): Promise<void> {
    if (!this.currentUserId) {
      console.warn('FavoritesService: No hay usuario activo');
      return;
    }

    try {
      console.log('FavoritesService: Configurando listener en tiempo real');
      
      // Configurar listener de Firestore
      this.unsubscribe = firebase.firestore()
        .collection('favorites')
        .where('userid', '==', this.currentUserId)
        .onSnapshot(
          (snapshot: firebase.firestore.QuerySnapshot) => {
            console.log('FavoritesService: Cambios detectados en favoritos');
            this.handleFavoritesUpdate(snapshot);
          },
          (error: any) => {
            console.error('FavoritesService: Error en listener:', error);
          }
        );
    } catch (error) {
      console.error('FavoritesService: Error al configurar listener:', error);
    }
  }

  /**
   * Manejar actualizaciones de favoritos desde Firestore
   */
  private async handleFavoritesUpdate(snapshot: firebase.firestore.QuerySnapshot): Promise<void> {
    try {
      const favorites: string[] = [];
      
      snapshot.forEach((doc: firebase.firestore.QueryDocumentSnapshot) => {
        const data = doc.data();
        if (data && data.sessionid) {
          favorites.push(data.sessionid.toString());
        }
      });

      console.log('FavoritesService: Favoritos actualizados:', favorites);

      // Guardar en Storage local
      const favoritesString = favorites.join(',');
      await Storage.set({ key: 'FAVORITOS', value: favoritesString });

      // Notificar a todos los listeners
      this.notifyListeners(favorites);
    } catch (error) {
      console.error('FavoritesService: Error al procesar favoritos:', error);
    }
  }

  /**
   * Agregar favorito
   */
  async addFavorite(sessionId: string): Promise<void> {
    if (!this.currentUserId) {
      console.warn('FavoritesService: No hay usuario activo');
      return;
    }

    try {
      console.log('FavoritesService: Agregando favorito:', sessionId);
      
      // Verificar si ya existe
      const exists = await this.checkIfFavoriteExists(sessionId);
      if (exists) {
        console.log('FavoritesService: Favorito ya existe');
        return;
      }

      // Agregar a Firestore
      await firebase.firestore().collection('favorites').add({
        sessionid: sessionId,
        userid: this.currentUserId,
        created_at: firebase.firestore.FieldValue.serverTimestamp()
      });

      console.log('FavoritesService: Favorito agregado exitosamente');
    } catch (error) {
      console.error('FavoritesService: Error al agregar favorito:', error);
      throw error;
    }
  }

  /**
   * Remover favorito
   */
  async removeFavorite(sessionId: string): Promise<void> {
    if (!this.currentUserId) {
      console.warn('FavoritesService: No hay usuario activo');
      return;
    }

    try {
      console.log('FavoritesService: Removiendo favorito:', sessionId);
      
      // Buscar y eliminar de Firestore
      const snapshot = await firebase.firestore()
        .collection('favorites')
        .where('userid', '==', this.currentUserId)
        .where('sessionid', '==', sessionId)
        .get();

      const deletePromises = snapshot.docs.map((doc: firebase.firestore.QueryDocumentSnapshot) => doc.ref.delete());
      await Promise.all(deletePromises);

      console.log('FavoritesService: Favorito removido exitosamente');
    } catch (error) {
      console.error('FavoritesService: Error al remover favorito:', error);
      throw error;
    }
  }

  /**
   * Verificar si un favorito ya existe
   */
  private async checkIfFavoriteExists(sessionId: string): Promise<boolean> {
    if (!this.currentUserId) return false;

    try {
      const snapshot = await firebase.firestore()
        .collection('favorites')
        .where('userid', '==', this.currentUserId)
        .where('sessionid', '==', sessionId)
        .get();

      return !snapshot.empty;
    } catch (error) {
      console.error('FavoritesService: Error al verificar favorito:', error);
      return false;
    }
  }

  /**
   * Obtener favoritos actuales desde Storage local
   */
  async getCurrentFavorites(): Promise<string[]> {
    try {
      const favoritesData = await Storage.get({ key: 'FAVORITOS' });
      const favoritesString = favoritesData.value || '';
      
      if (!favoritesString) return [];
      
      return favoritesString.split(',').filter((id: string) => id.trim() !== '');
    } catch (error) {
      console.error('FavoritesService: Error al obtener favoritos:', error);
      return [];
    }
  }

  /**
   * Agregar listener para cambios en favoritos
   */
  addListener(callback: (favorites: string[]) => void): void {
    this.listeners.push(callback);
  }

  /**
   * Remover listener
   */
  removeListener(callback: (favorites: string[]) => void): void {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  /**
   * Notificar a todos los listeners
   */
  private notifyListeners(favorites: string[]): void {
    this.listeners.forEach(listener => {
      try {
        listener(favorites);
      } catch (error) {
        console.error('FavoritesService: Error en listener:', error);
      }
    });
  }

  /**
   * Forzar sincronización manual
   */
  async forcSync(): Promise<void> {
    if (!this.currentUserId) {
      console.warn('FavoritesService: No hay usuario activo para sincronizar');
      return;
    }

    try {
      console.log('FavoritesService: Forzando sincronización');
      
      // Obtener favoritos desde Firestore
      const snapshot = await firebase.firestore()
        .collection('favorites')
        .where('userid', '==', this.currentUserId)
        .get();

      // Procesar y guardar
      await this.handleFavoritesUpdate(snapshot);
    } catch (error) {
      console.error('FavoritesService: Error en sincronización forzada:', error);
    }
  }

  /**
   * Limpiar el servicio (logout)
   */
  cleanup(): void {
    console.log('FavoritesService: Limpiando servicio');
    
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    
    this.currentUserId = null;
    this.listeners = [];
  }

  /**
   * Verificar si el servicio está activo
   */
  isActive(): boolean {
    return this.currentUserId !== null && this.unsubscribe !== null;
  }
}

export default FavoritesService;
